// src/pages/MopsDashboard.jsx
//
// Missed Obligation Points (MOPs) — Dean of Students dashboard.
// King's Academy Faculty Tool Kit
//
// Reads:
//   /config/mopSeasons                    season list
//   /config/mops                          engine settings + threshold ladder
//   /mopSummaries/{seasonId}              per-student totals (materialized nightly)
//   /mopSummaries/{seasonId}/events       individual MOP events
//
// Calls:
//   rebuildMopSummary, addManualMop, setManualMopVoided
//
// Thresholds are read from /config/mops.thresholds. Until that field exists,
// the ladder stays neutral — no banding, no consequence labels.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import StudyHallBoard from '../components/StudyHallBoard';
import {
  downloadStudentReport,
  printStudentReport,
  downloadTeacherReport,
  printTeacherReport,
} from '../utils/mopsReportPdf';
import { db } from '../config/firebase';

/* ------------------------------------------------------------------ */
/* Brand tokens                                                        */
/* ------------------------------------------------------------------ */

const KA = {
  red: '#E83530',
  navy: '#233C5F',
  gold: '#A29164',
  ink: '#1a1a1a',
  muted: '#6b7280',
  line: '#e5e7eb',
  wash: '#f8f8f7',
  white: '#ffffff',
};

const BODY = "'Helvetica Neue', Helvetica, Arial, sans-serif";
const DISPLAY = 'Georgia, serif';

// Mirrors SUPERADMIN_UID in functions/mops.js.
const SUPERADMIN_UID = 'IOj8t19zqBRJynu45HRKqf8J0MB2';

// Must stay in step with the writeAccess check in assertMopsAccess(). If
// these drift, the UI offers buttons the server then refuses.
const WRITE_ROLES = ['dean', 'admin', 'superadmin'];

const FREE_BLOCKS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

const MANUAL_CATEGORIES = [
  'Dress code',
  'Missed study hall',
  'Missed assembly',
  'Missed advisory',
  'Missed athletics/co-curricular',
  'Room check',
  'Other',
];

/* ------------------------------------------------------------------ */
/* Small presentational helpers                                        */
/* ------------------------------------------------------------------ */

function Stat({ label, value, tone, sub }) {
  return (
    <div style={{ padding: '12px 20px 12px 0', minWidth: 110 }}>
      <div
        style={{
          fontSize: 26,
          fontFamily: DISPLAY,
          color: tone || KA.navy,
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: KA.muted,
          marginTop: 4,
        }}
      >
        {label}
      </div>
      {/* Points answer "how much", this answers "how many students" — the
          two diverge sharply once a few students carry most of the total. */}
      {sub != null && (
        <div style={{ fontSize: 11, color: KA.navy, marginTop: 3, fontWeight: 600 }}>{sub}</div>
      )}
    </div>
  );
}

function Btn({ children, onClick, disabled, variant = 'primary', size = 'md', title }) {
  const base = {
    fontFamily: BODY,
    fontWeight: 600,
    borderRadius: 4,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    padding: size === 'sm' ? '5px 10px' : '8px 16px',
    fontSize: size === 'sm' ? 12 : 13,
    transition: 'opacity .15s',
  };
  const variants = {
    primary: { background: KA.red, color: KA.white, border: 'none' },
    outline: { background: KA.white, color: KA.navy, border: `1px solid ${KA.navy}` },
    ghost: { background: 'transparent', color: KA.muted, border: `1px solid ${KA.line}` },
  };
  return (
    <button title={title} onClick={onClick} disabled={disabled} style={{ ...base, ...variants[variant] }}>
      {children}
    </button>
  );
}

// NOTE: deliberately a <div>, not a <label>. Some fields wrap interactive
// children (the student typeahead), and a browser forwards any click inside
// a <label> to that label's form control - which swallowed the click on the
// suggestion list and made selection silently impossible.
function Field({ label, children }) {
  return (
    <div style={{ display: 'block', marginBottom: 12 }}>
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: KA.muted,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '7px 9px',
  border: `1px solid ${KA.line}`,
  borderRadius: 4,
  fontFamily: BODY,
  fontSize: 13,
  boxSizing: 'border-box',
};

/* ------------------------------------------------------------------ */
/* Threshold resolution                                                */
/* ------------------------------------------------------------------ */

/**
 * thresholds: [{ points: 4, label: 'Advisor notified', color: '#A29164' }, ...]
 * Returns the highest threshold a total has reached, or null.
 */
function bandFor(total, thresholds) {
  if (!Array.isArray(thresholds) || thresholds.length === 0) return null;
  const hit = thresholds
    .filter((t) => Number(total) >= Number(t.points))
    .sort((a, b) => Number(b.points) - Number(a.points))[0];
  return hit || null;
}

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */

export default function MopsDashboard() {
  // Authoritative write check.
  //
  // The context shape above is a guess at where AuthContext exposes the
  // role, and a wrong guess silently hides the buttons rather than erroring.
  // So read /users/{uid}.role straight from Firestore — the exact document
  // and field assertMopsAccess() checks server-side. Firestore rules already
  // allow any signed-in user to read their own user doc, so this needs no
  // new permission.
  //
  // The context value is still used as a fast path so the buttons appear
  // immediately when it happens to be right; the read only ever adds access.
  const [roleWrite, setRoleWrite] = useState(null); // null = still resolving

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const current = getAuth().currentUser;
        if (!current) {
          if (alive) setRoleWrite(false);
          return;
        }
        if (current.uid === SUPERADMIN_UID) {
          if (alive) setRoleWrite(true);
          return;
        }
        const snap = await getDoc(doc(db, 'users', current.uid));
        const r = String((snap.exists() ? snap.data().role : '') || '').toLowerCase();
        if (alive) setRoleWrite(WRITE_ROLES.includes(r));
      } catch (err) {
        // A failed read must not grant access, and must not break the page.
        if (alive) setRoleWrite(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Exactly the server's answer, nothing else. Deliberately no "fast path"
  // off the auth context: that context is what was wrong in the first place,
  // and a context that disagrees with the user doc would show buttons the
  // server then refuses. Buttons stay hidden for the one read it takes to
  // find out, which is the honest order.
  const canWrite = roleWrite === true;

  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState('');
  const [config, setConfig] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [notice, setNotice] = useState(null);

  // table controls
  const [search, setSearch] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [advisorFilter, setAdvisorFilter] = useState('');
  const [blockFilter, setBlockFilter] = useState('');
  const [minPoints, setMinPoints] = useState(1);
  const [sortKey, setSortKey] = useState('total');
  const [sortDir, setSortDir] = useState('desc');

  // drill-down
  const [selected, setSelected] = useState(null); // personId
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // manual entry
  const [showAdd, setShowAdd] = useState(false);

  const [showPhonePreview, setShowPhonePreview] = useState(false);
  const [showStudyHall, setShowStudyHall] = useState(false);
  const [showTeachers, setShowTeachers] = useState(false);
  // { name, studentIds } — set by clicking a teacher in the By-teacher view.
  const [teacherFilter, setTeacherFilter] = useState(null);

  // threshold notification queue
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [acting, setActing] = useState(null); // notificationId being acted on
  const [voiding, setVoiding] = useState(null); // entryId being voided

  const fns = useMemo(() => getFunctions(undefined, 'us-central1'), []);

  /* ---------------- initial config load ---------------- */

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [seasonSnap, cfgSnap] = await Promise.all([
          getDoc(doc(db, 'config', 'mopSeasons')),
          getDoc(doc(db, 'config', 'mops')),
        ]);
        if (!alive) return;

        const list =
          seasonSnap.exists() && Array.isArray(seasonSnap.data().seasons)
            ? seasonSnap.data().seasons.filter((s) => s && s.id)
            : [];
        setSeasons(list);
        setConfig(cfgSnap.exists() ? cfgSnap.data() : {});

        // default to the season containing today, else the last dated one
        const today = new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);
        const active = list.find(
          (s) => s.startDate && today >= s.startDate && (!s.endDate || today <= s.endDate)
        );
        const dated = [...list].filter((s) => s.startDate).sort((a, b) => (a.startDate < b.startDate ? 1 : -1));
        setSeasonId((active && active.id) || (dated[0] && dated[0].id) || (list[0] && list[0].id) || '');
      } catch (err) {
        if (alive) setError(`Could not load MOPs configuration. ${err.message}`);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /* ---------------- summary load ---------------- */

  const loadSummary = useCallback(async (sid) => {
    if (!sid) {
      setSummary(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const snap = await getDoc(doc(db, 'mopSummaries', sid));
      setSummary(snap.exists() ? snap.data() : null);
    } catch (err) {
      setError(`Could not load the summary for this season. ${err.message}`);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Single equality filter, then filter by status in memory - avoids needing
  // a composite index for what is only ever a handful of docs.
  const loadNotifications = useCallback(async (sid) => {
    if (!sid) {
      setNotifications([]);
      return;
    }
    setNotifLoading(true);
    try {
      const q = query(collection(db, 'mopNotifications'), where('seasonId', '==', sid));
      const snap = await getDocs(q);
      const rows = [];
      snap.forEach((d) => rows.push({ _id: d.id, ...d.data() }));
      // The two ladders number their rungs differently (MOP levels 1-3,
      // phone strikes 3/6/9), so compare kind first — a Disciplinary Report
      // must never sort below a phone 3-strike just because 3 > 3.
      const kindRank = (n) => (n.kind === 'phone' ? 1 : 0);
      rows.sort(
        (a, b) =>
          kindRank(a) - kindRank(b) ||
          (b.level || 0) - (a.level || 0) ||
          (b.currentTotal || 0) - (a.currentTotal || 0)
      );
      setNotifications(rows);
    } catch (err) {
      setError(`Could not load the notification queue. ${err.message}`);
      setNotifications([]);
    } finally {
      setNotifLoading(false);
    }
  }, []);

  useEffect(() => {
    setSelected(null);
    setEvents([]);
    loadSummary(seasonId);
    loadNotifications(seasonId);
  }, [seasonId, loadSummary, loadNotifications]);

  /* ---------------- rebuild ---------------- */

  async function handleRebuild() {
    setRebuilding(true);
    setNotice(null);
    setError(null);
    try {
      const fn = httpsCallable(fns, 'rebuildMopSummary');
      const res = await fn({ seasonId });
      const d = res.data || {};
      setNotice(
        `Recalculated ${d.eventCount ?? 0} points across ${d.studentCount ?? 0} students from ` +
          `${d.datesScanned ?? 0} school days.` +
          (d.unresolvedStudentIdCount
            ? ` ${d.unresolvedStudentIdCount} student IDs had no matching record in /students.`
            : '')
      );
      await loadSummary(seasonId);
      await loadNotifications(seasonId);
      if (selected) await loadEvents(selected);
    } catch (err) {
      setError(`Recalculation failed. ${err.message}`);
    } finally {
      setRebuilding(false);
    }
  }

  /* ---------------- events drill-down ---------------- */

  const loadEvents = useCallback(
    async (personId) => {
      if (!seasonId || !personId) return;
      setEventsLoading(true);
      try {
        const q = query(
          collection(db, 'mopSummaries', seasonId, 'events'),
          where('personId', '==', String(personId))
        );
        const snap = await getDocs(q);
        const rows = [];
        snap.forEach((d) => rows.push({ _id: d.id, ...d.data() }));
        rows.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
        setEvents(rows);
      } catch (err) {
        setError(`Could not load this student's history. ${err.message}`);
        setEvents([]);
      } finally {
        setEventsLoading(false);
      }
    },
    [seasonId]
  );

  function openStudent(personId) {
    setSelected(personId);
    loadEvents(personId);
  }

  function closeStudent() {
    setSelected(null);
    setEvents([]);
  }

  // Voiding writes to /mopEntries, but totals are materialized - so without a
  // rebuild the row stays on screen and the button looks broken. Void, then
  // recompute, then refresh, so the click does what it appears to do.
  async function handleVoid(entryId) {
    if (!entryId) {
      setError('That point has no entry ID, so it cannot be voided. Tardies are corrected in Veracross.');
      return;
    }
    if (!window.confirm('Void this point? It will stop counting toward the student\u2019s total.')) {
      return;
    }

    setVoiding(entryId);
    setError(null);
    setNotice(null);
    try {
      const voidFn = httpsCallable(fns, 'setManualMopVoided');
      await voidFn({ entryId, voided: true });

      const rebuildFn = httpsCallable(fns, 'rebuildMopSummary');
      await rebuildFn({ seasonId });

      await loadSummary(seasonId);
      await loadNotifications(seasonId);
      if (selected) await loadEvents(selected);

      setNotice('Point voided and totals updated.');
    } catch (err) {
      setError(`Could not void that point. ${err.message}`);
    } finally {
      setVoiding(null);
    }
  }

  async function handleNotificationDecision(notificationId, status) {
    setActing(notificationId);
    setError(null);
    setNotice(null);
    try {
      const fn = httpsCallable(fns, 'setMopNotificationStatus');
      await fn({ notificationId, status });
      setNotice(
        status === 'approved'
          ? 'Approved. Nothing has been emailed yet - sending is not wired up.'
          : 'Dismissed. This crossing will not be raised again.'
      );
      await loadNotifications(seasonId);
    } catch (err) {
      setError(err.message);
    } finally {
      setActing(null);
    }
  }

  /* ---------------- derived table data ---------------- */

  const thresholds = config.thresholds;
  // Prefer what the rebuild actually used (stored on the summary) over the
  // live config, so the table matches the numbers it is displaying.
  const phoneRungs = (summary && summary.phoneStrikeThresholds) || config.phoneStrikeThresholds;

  const students = useMemo(() => {
    const map = (summary && summary.students) || {};
    return Object.values(map);
  }, [summary]);

  // How many distinct students carry at least one of each kind. Season-wide,
  // deliberately not affected by the table filters below — these are the
  // headline figures, and having them move while searching a name reads as
  // a bug rather than a feature.
  const offenders = useMemo(() => {
    const n = (k) => students.filter((s) => Number(s[k]) > 0).length;
    return {
      tardy: n('tardyPoints'),
      phone: n('phonePoints'),
      manual: n('manualPoints'),
      phoneAtRung:
        phoneRungs && phoneRungs.length
          ? students.filter(
              (s) => Number(s.phoneStrikes) >= Number(phoneRungs[0].strikes)
            ).length
          : 0,
    };
  }, [students, phoneRungs]);

  const grades = useMemo(
    () => Array.from(new Set(students.map((s) => s.grade).filter((g) => g != null && g !== ''))).sort(),
    [students]
  );
  const advisors = useMemo(
    () => Array.from(new Set(students.map((s) => s.advisorName).filter(Boolean))).sort(),
    [students]
  );

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    let out = students.filter((s) => {
      if (Number(s.total) < Number(minPoints)) return false;
      if (gradeFilter && String(s.grade) !== String(gradeFilter)) return false;
      if (advisorFilter && s.advisorName !== advisorFilter) return false;
      if (teacherFilter && !teacherFilter.ids.has(String(s.personId))) return false;
      if (blockFilter === 'none' && s.freeBlock) return false;
      if (blockFilter && blockFilter !== 'none' && s.freeBlock !== blockFilter) return false;
      if (term) {
        const hay = `${s.person || ''} ${s.personId || ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    out.sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (sortKey === 'person') {
        av = String(av || '').toLowerCase();
        bv = String(bv || '').toLowerCase();
        return av < bv ? -dir : av > bv ? dir : 0;
      }
      av = av == null ? -1 : av;
      bv = bv == null ? -1 : bv;
      if (av === bv) return String(a.person || '').localeCompare(String(b.person || ''));
      return av < bv ? -dir : dir;
    });
    return out;
  }, [students, search, gradeFilter, advisorFilter, blockFilter, teacherFilter, minPoints, sortKey, sortDir]);

  function toggleSort(key) {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDir(key === 'person' ? 'asc' : 'desc');
    }
  }

  function exportCsv() {
    const head = ['Student', 'ID', 'Grade', 'Advisor', 'Free block', 'Tardy points',
                  'Phone points', 'Phone strikes', 'Other points', 'Total', 'Most recent'];
    const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const body = rows.map((r) =>
      [r.person, r.personId, r.grade, r.advisorName, r.freeBlock || 'none', r.tardyPoints,
       r.phonePoints, r.phoneStrikes, r.manualPoints, r.total, r.lastDate]
        .map(esc)
        .join(',')
    );
    const blob = new Blob([[head.map(esc).join(','), ...body].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mops-${seasonId}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /* ---------------- render ---------------- */

  const activeSeason = seasons.find((s) => s.id === seasonId);

  return (
    <div style={{ fontFamily: BODY, color: KA.ink, padding: 24, maxWidth: 1320, margin: '0 auto' }}>
      {/* ---------- header ---------- */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16,
          borderBottom: `3px solid ${KA.navy}`,
          paddingBottom: 14,
        }}
      >
        <div>
          <h1 style={{ fontFamily: DISPLAY, color: KA.navy, margin: 0, fontSize: 30, fontWeight: 400 }}>
            Missed Obligation Points
          </h1>
          <div style={{ color: KA.muted, fontSize: 13, marginTop: 4 }}>
            Upper School · Dean of Students
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={seasonId}
            onChange={(e) => setSeasonId(e.target.value)}
            style={{ ...inputStyle, width: 'auto', minWidth: 160 }}
          >
            {seasons.length === 0 && <option value="">No seasons configured</option>}
            {seasons.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label || s.id}
                {!s.startDate ? ' — dates not set' : ''}
              </option>
            ))}
          </select>
          <Btn onClick={() => setShowTeachers(true)} variant="outline" disabled={!summary}>
            By teacher
          </Btn>
          <Btn onClick={() => setShowStudyHall(true)} variant="outline" disabled={!summary}>
            Study hall
          </Btn>
          {canWrite && (
            <Btn onClick={() => setShowAdd(true)} variant="outline" disabled={!seasonId}>
              Add a point
            </Btn>
          )}
          {canWrite && (
            <Btn onClick={handleRebuild} disabled={rebuilding || !seasonId}>
              {rebuilding ? 'Recalculating…' : 'Recalculate'}
            </Btn>
          )}
        </div>
      </div>

      {/* ---------- messages ---------- */}
      {notice && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 14px',
            background: '#f2f6ee',
            borderLeft: `3px solid ${KA.gold}`,
            fontSize: 13,
          }}
        >
          {notice}
        </div>
      )}
      {error && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 14px',
            background: '#fdf0ef',
            borderLeft: `3px solid ${KA.red}`,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* ---------- season without dates ---------- */}
      {activeSeason && !activeSeason.startDate && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 14px',
            background: KA.wash,
            borderLeft: `3px solid ${KA.gold}`,
            fontSize: 13,
          }}
        >
          {activeSeason.label || seasonId} has no start date yet. Set one in{' '}
          <code>/config/mopSeasons</code> and recalculate to begin tracking.
        </div>
      )}

      {/* ---------- stats ---------- */}
      {loading ? (
        <div style={{ padding: 40, color: KA.muted }}>Loading…</div>
      ) : !summary ? (
        <EmptyState canWrite={canWrite} onRebuild={handleRebuild} rebuilding={rebuilding} seasonId={seasonId} />
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8,
              marginTop: 8,
              borderBottom: `1px solid ${KA.line}`,
            }}
          >
            <Stat label="Students" value={summary.studentCount ?? 0} />
            <Stat label="Total points" value={summary.totalPoints ?? 0} tone={KA.red} />
            <Stat label="School days" value={summary.datesScanned ?? 0} />
            <Stat
              label="Tardies"
              value={students.reduce((s, r) => s + (Number(r.tardyPoints) || 0), 0)}
              sub={`${offenders.tardy} student${offenders.tardy === 1 ? '' : 's'}`}
            />
            <Stat
              label="Phone"
              value={students.reduce((s, r) => s + (Number(r.phonePoints) || 0), 0)}
              sub={
                `${offenders.phone} student${offenders.phone === 1 ? '' : 's'}` +
                (phoneRungs && phoneRungs.length && offenders.phoneAtRung
                  ? ` · ${offenders.phoneAtRung} at ${phoneRungs[0].strikes}+`
                  : '')
              }
            />
            <Stat
              label="Other"
              value={students.reduce((s, r) => s + (Number(r.manualPoints) || 0), 0)}
              sub={`${offenders.manual} student${offenders.manual === 1 ? '' : 's'}`}
            />
            <div style={{ marginLeft: 'auto', fontSize: 11, color: KA.muted, textAlign: 'right' }}>
              Last recalculated
              <br />
              {summary.rebuiltAt ? new Date(summary.rebuiltAt).toLocaleString() : '—'}
            </div>
          </div>

          {!thresholds && (
            <div style={{ fontSize: 12, color: KA.muted, marginTop: 10 }}>
              Thresholds aren't defined yet. Add a <code>thresholds</code> array to{' '}
              <code>/config/mops</code> to show consequence bands on this table.
            </div>
          )}

          {Array.isArray(summary.unresolvedStudentIds) && summary.unresolvedStudentIds.length > 0 && (
            <div
              style={{
                marginTop: 12,
                padding: '10px 14px',
                background: '#fdf0ef',
                borderLeft: `3px solid ${KA.red}`,
                fontSize: 13,
              }}
            >
              {summary.unresolvedStudentIds.length} student ID
              {summary.unresolvedStudentIds.length === 1 ? '' : 's'} in the attendance data had no
              matching record in <code>/students</code>, so their points aren't being counted.
              Usually a student imported since the last recalculation, or a document ID that
              isn't the SIS ID. If they shouldn't be tracked at all, add the ID to{' '}
              <code>ignoredPersonIds</code> in <code>/config/mops</code>.{' '}
              {summary.unresolvedStudentIds.slice(0, 6).join(', ')}
            </div>
          )}

          {/* ---------- threshold notification queue ---------- */}
          <NotificationQueue
            notifications={notifications}
            loading={notifLoading}
            canWrite={canWrite}
            acting={acting}
            onDecide={handleNotificationDecision}
            onOpenStudent={openStudent}
          />

          {/* ---------- controls ---------- */}
          <div
            style={{
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
              alignItems: 'center',
              margin: '18px 0 10px',
            }}
          >
            <input
              placeholder="Search name or ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...inputStyle, width: 200 }}
            />
            <select value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
              <option value="">All grades</option>
              {grades.map((g) => (
                <option key={g} value={g}>
                  Grade {g}
                </option>
              ))}
            </select>
            <select
              value={blockFilter}
              onChange={(e) => setBlockFilter(e.target.value)}
              style={{ ...inputStyle, width: 'auto' }}
            >
              <option value="">All free blocks</option>
              {FREE_BLOCKS.map((b) => (
                <option key={b} value={b}>
                  Block {b}
                </option>
              ))}
              <option value="none">No free block</option>
            </select>
            <select value={advisorFilter} onChange={(e) => setAdvisorFilter(e.target.value)} style={{ ...inputStyle, width: 'auto', maxWidth: 200 }}>
              <option value="">All advisors</option>
              {advisors.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
            <label style={{ fontSize: 12, color: KA.muted, display: 'flex', alignItems: 'center', gap: 6 }}>
              Minimum
              <input
                type="number"
                min={0}
                value={minPoints}
                onChange={(e) => setMinPoints(Number(e.target.value) || 0)}
                style={{ ...inputStyle, width: 64 }}
              />
            </label>
            {teacherFilter && (
              <span
                style={{
                  fontSize: 12,
                  padding: '4px 10px',
                  borderRadius: 12,
                  background: '#eef2f8',
                  color: KA.navy,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                title="Clear this filter"
                onClick={() => setTeacherFilter(null)}
              >
                Tardies from {teacherFilter.name} ✕
              </span>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: KA.muted }}>
                {rows.length} of {students.length}
              </span>
              {canWrite && (
                <Btn variant="ghost" size="sm" onClick={() => setShowPhonePreview(true)}>
                  Phone history
                </Btn>
              )}
              <Btn variant="ghost" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
                Export CSV
              </Btn>
            </div>
          </div>

          {/* ---------- table ---------- */}
          <div style={{ border: `1px solid ${KA.line}`, borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 760 }}>
                <thead>
                  <tr style={{ background: KA.wash, textAlign: 'left' }}>
                    {/* Position in the CURRENT sort/filter — a scroll marker,
                        not an identifier. Renumbers whenever the view changes. */}
                    <Th align="right">#</Th>
                    <Th onClick={() => toggleSort('person')} active={sortKey === 'person'} dir={sortDir}>
                      Student
                    </Th>
                    <Th>ID</Th>
                    <Th onClick={() => toggleSort('grade')} active={sortKey === 'grade'} dir={sortDir}>
                      Grade
                    </Th>
                    <Th>Advisor</Th>
                    <Th onClick={() => toggleSort('tardyPoints')} active={sortKey === 'tardyPoints'} dir={sortDir} align="right">
                      Tardies
                    </Th>
                    <Th onClick={() => toggleSort('phoneStrikes')} active={sortKey === 'phoneStrikes'} dir={sortDir} align="right">
                      Phone
                    </Th>
                    <Th onClick={() => toggleSort('freeBlock')} active={sortKey === 'freeBlock'} dir={sortDir}>
                      Free
                    </Th>
                    <Th onClick={() => toggleSort('manualPoints')} active={sortKey === 'manualPoints'} dir={sortDir} align="right">
                      Other
                    </Th>
                    <Th onClick={() => toggleSort('total')} active={sortKey === 'total'} dir={sortDir} align="right">
                      Total
                    </Th>
                    <Th onClick={() => toggleSort('lastDate')} active={sortKey === 'lastDate'} dir={sortDir}>
                      Most recent
                    </Th>
                    {thresholds && <Th>Status</Th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={thresholds ? 12 : 11} style={{ padding: 28, textAlign: 'center', color: KA.muted }}>
                        No students match these filters.
                      </td>
                    </tr>
                  )}
                  {rows.map((r, i) => {
                    const band = bandFor(r.total, thresholds);
                    const isSel = String(r.personId) === String(selected);
                    return (
                      <React.Fragment key={r.personId}>
                      <tr
                        onClick={() => (isSel ? closeStudent() : openStudent(r.personId))}
                        style={{
                          borderTop: `1px solid ${KA.line}`,
                          cursor: 'pointer',
                          background: isSel ? '#f4f7fb' : KA.white,
                        }}
                      >
                        <Td
                          align="right"
                          style={{ color: KA.muted, fontVariantNumeric: 'tabular-nums', width: 1, whiteSpace: 'nowrap' }}
                        >
                          {i + 1}
                        </Td>
                        <Td style={{ fontWeight: 600, color: KA.navy }}>{r.person || '—'}</Td>
                        <Td style={{ color: KA.muted, fontVariantNumeric: 'tabular-nums' }}>{r.personId}</Td>
                        <Td>{r.grade ?? '—'}</Td>
                        <Td style={{ color: KA.muted }}>{r.advisorName || '—'}</Td>
                        <Td align="right">{r.tardyPoints || 0}</Td>
                        <Td align="right" title={`${r.phoneStrikes || 0} phone violations`}>
                          {r.phonePoints || 0}
                        </Td>
                        <Td>
                          {r.freeBlock ? (
                            <span style={{ fontWeight: 600, color: KA.navy }}>{r.freeBlock}</span>
                          ) : (
                            <span
                              style={{ color: KA.muted, fontSize: 11 }}
                              title="No free period — losing one is not available as a consequence"
                            >
                              none
                            </span>
                          )}
                        </Td>
                        <Td align="right">{r.manualPoints || 0}</Td>
                        <Td align="right" style={{ fontWeight: 700, color: band ? band.color || KA.red : KA.ink }}>
                          {r.total}
                        </Td>
                        <Td style={{ color: KA.muted }}>{r.lastDate || '—'}</Td>
                        {thresholds && (
                          <Td>
                            {band ? (
                              <span
                                style={{
                                  fontSize: 11,
                                  padding: '2px 8px',
                                  borderRadius: 10,
                                  background: (band.color || KA.red) + '1a',
                                  color: band.color || KA.red,
                                  fontWeight: 600,
                                }}
                              >
                                {band.label}
                              </span>
                            ) : (
                              <span style={{ color: KA.muted, fontSize: 12 }}>—</span>
                            )}
                          </Td>
                        )}
                      </tr>

                      {/* Expands directly beneath the row that was clicked,
                          rather than below the whole table — on a 250-row
                          roster the old bottom panel meant scrolling past
                          everything to read one student. */}
                      {isSel && (
                        <tr>
                          <td
                            colSpan={thresholds ? 12 : 11}
                            style={{ padding: 0, background: '#f4f7fb' }}
                          >
                            <StudentDetail
                              inline
                              student={r}
                              personId={r.personId}
                              events={events}
                              loading={eventsLoading}
                              canWrite={canWrite}
                              voiding={voiding}
                              onClose={closeStudent}
                              onVoid={handleVoid}
                              reportCtx={{
                                season: activeSeason || { id: seasonId },
                                thresholds,
                                phoneRungs,
                                generatedBy:
                                  (getAuth().currentUser && getAuth().currentUser.email) || null,
                              }}
                            />
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ---------- manual entry ---------- */}
      {showTeachers && (
        <TeacherTardyModal
          teacherTardies={summary && summary.teacherTardies}
          season={activeSeason || { id: seasonId }}
          onClose={() => setShowTeachers(false)}
          onFilter={(name, ids) => {
            setTeacherFilter({ name, ids: new Set(ids.map(String)) });
            setShowTeachers(false);
          }}
        />
      )}

      {showStudyHall && (
        <div
          onClick={() => setShowStudyHall(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(26,26,26,0.45)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: 20,
            zIndex: 1000,
            overflowY: 'auto',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: KA.white,
              borderRadius: 6,
              borderTop: `3px solid ${KA.navy}`,
              padding: 24,
              width: '100%',
              maxWidth: 1000,
              margin: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: -12 }}>
              <Btn variant="ghost" size="sm" onClick={() => setShowStudyHall(false)}>
                Close
              </Btn>
            </div>
            <StudyHallBoard seasonId={seasonId} canWrite={canWrite} embedded />
          </div>
        </div>
      )}

      {showPhonePreview && (
        <PhonePreviewModal
          fns={fns}
          seasonId={seasonId}
          seasonStart={activeSeason ? activeSeason.startDate : null}
          onClose={() => setShowPhonePreview(false)}
        />
      )}

      {showAdd && (
        <AddMopModal
          fns={fns}
          seasonLabel={activeSeason ? activeSeason.label || seasonId : seasonId}
          onClose={() => setShowAdd(false)}
          onSaved={(msg) => {
            setShowAdd(false);
            setNotice(msg);
          }}
          onError={(msg) => setError(msg)}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Table cells                                                         */
/* ------------------------------------------------------------------ */

function Th({ children, onClick, active, dir, align }) {
  return (
    <th
      onClick={onClick}
      style={{
        padding: '10px 12px',
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: active ? KA.navy : KA.muted,
        fontWeight: 600,
        cursor: onClick ? 'pointer' : 'default',
        textAlign: align || 'left',
        whiteSpace: 'nowrap',
        userSelect: 'none',
      }}
    >
      {children}
      {active && <span style={{ marginLeft: 4 }}>{dir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );
}

function Td({ children, style, align }) {
  return (
    <td style={{ padding: '9px 12px', textAlign: align || 'left', ...style }}>{children}</td>
  );
}

/* ------------------------------------------------------------------ */
/* Threshold notification queue                                        */
/* ------------------------------------------------------------------ */

const ROLE_LABELS = {
  student: 'Student',
  parents: 'Parents',
  advisor: 'Advisor',
  classDean: 'Class dean',
  houseHead: 'House head',
};

function NotificationQueue({ notifications, loading, canWrite, acting, onDecide, onOpenStudent }) {
  // Collapsed on every mount, deliberately not persisted. This block sits
  // above the roster, and a term's worth of crossings is several screens of
  // cards between the Dean and the student list he came here for.
  const [open, setOpen] = useState(false);
  const [showResolved, setShowResolved] = useState(false);

  const pending = notifications.filter((n) => n.status === 'pending');
  const resolved = notifications.filter((n) => n.status !== 'pending');
  const shown = showResolved ? notifications : pending;

  // Severity breakdown for the collapsed summary, most serious first, so
  // "1 Disciplinary Report" leads rather than trailing a pile of warnings.
  const byLevel = [];
  pending.forEach((n) => {
    const label = n.thresholdLabel || `Level ${n.level}`;
    const hit = byLevel.find((b) => b.label === label);
    if (hit) hit.count += 1;
    else
      byLevel.push({
        label,
        count: 1,
        level: Number(n.level) || 0,
        kindRank: n.kind === 'phone' ? 1 : 0,
      });
  });
  // MOP ladder first, then phone — the rung numbers are not comparable
  // across the two (MOP levels 1-3 vs phone strikes 3/6/9).
  byLevel.sort((a, b) => a.kindRank - b.kindRank || b.level - a.level);

  const needsData = pending.filter((n) => n.readyToSend === false).length;
  const belowNow = pending.filter((n) => n.belowThresholdNow === true).length;
  const hasPending = pending.length > 0;

  if (loading) {
    return <div style={{ padding: 16, color: KA.muted, fontSize: 13 }}>Loading threshold crossings…</div>;
  }
  if (notifications.length === 0) {
    return (
      <div style={{ marginTop: 18, fontSize: 13, color: KA.muted }}>
        No student has reached a consequence threshold this term.
      </div>
    );
  }

  return (
    <div style={{ marginTop: 20 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          textAlign: 'left',
          cursor: 'pointer',
          padding: '12px 16px',
          borderRadius: 6,
          fontFamily: BODY,
          border: `1px solid ${hasPending ? '#f2c9c7' : KA.line}`,
          borderLeft: `4px solid ${hasPending ? KA.red : KA.line}`,
          background: hasPending ? '#fdf0ef' : KA.wash,
        }}
      >
        <span
          style={{
            minWidth: 30,
            height: 30,
            padding: '0 9px',
            borderRadius: 15,
            flexShrink: 0,
            background: hasPending ? KA.red : KA.muted,
            color: KA.white,
            fontWeight: 700,
            fontSize: 14,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {pending.length}
        </span>

        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              fontSize: 14,
              fontWeight: 600,
              color: hasPending ? KA.red : KA.navy,
            }}
          >
            {hasPending
              ? `${pending.length} student${pending.length === 1 ? '' : 's'} ${
                  pending.length === 1 ? 'has' : 'have'
                } reached a consequence threshold`
              : 'No threshold crossings awaiting review'}
          </span>
          <span style={{ display: 'block', fontSize: 12, color: KA.muted, marginTop: 3 }}>
            {hasPending
              ? byLevel.map((b) => `${b.count} ${b.label}`).join('  ·  ')
              : `${resolved.length} resolved this term`}
            {needsData > 0 && `  ·  ${needsData} with incomplete contacts`}
            {belowNow > 0 && `  ·  ${belowNow} now below threshold`}
          </span>
        </span>

        <span style={{ fontSize: 12, fontWeight: 600, color: KA.navy, flexShrink: 0 }}>
          {open ? 'Hide' : 'Review'}
        </span>
        <span
          aria-hidden="true"
          style={{
            flexShrink: 0,
            fontSize: 10,
            color: KA.navy,
            display: 'inline-block',
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform .15s ease',
          }}
        >
          &#9654;
        </span>
      </button>

      {open && (
        <>
      {resolved.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Btn variant="ghost" size="sm" onClick={() => setShowResolved(!showResolved)}>
            {showResolved ? 'Show only pending' : `Show all (${resolved.length} resolved)`}
          </Btn>
        </div>
      )}

      {shown.length === 0 && (
        <div style={{ fontSize: 13, color: KA.muted, marginTop: 10 }}>
          Nothing awaiting review.
        </div>
      )}

      <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
        {shown.map((n) => {
          const missing = n.missingRecipients || [];
          const blocked = n.belowThresholdNow === true;
          const incomplete = missing.length > 0;
          const isPending = n.status === 'pending';
          const busy = acting === n._id;

          return (
            <div
              key={n._id}
              style={{
                border: `1px solid ${KA.line}`,
                borderLeft: `4px solid ${
                  blocked ? KA.muted : n.kind === 'phone' ? KA.gold : KA.red
                }`,
                borderRadius: 6,
                background: isPending ? KA.white : KA.wash,
                padding: 14,
                opacity: isPending ? 1 : 0.75,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: KA.navy }}>
                    <span
                      onClick={() => onOpenStudent(n.personId)}
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      {n.person || `Student ${n.personId}`}
                    </span>
                    {n.grade != null && (
                      <span style={{ color: KA.muted, fontWeight: 400 }}> · Grade {n.grade}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, marginTop: 4 }}>
                    <strong style={{ color: KA.red }}>{n.thresholdLabel}</strong>
                    <span style={{ color: KA.muted }}>
                      {' '}— reached {n.thresholdPoints}{' '}
                      {n.kind === 'phone' ? 'strikes' : 'points'} on{' '}
                      {n.crossedAt ? String(n.crossedAt).slice(0, 10) : '—'}
                      {n.currentTotal != null && ` · now at ${n.currentTotal}`}
                    </span>
                  </div>
                </div>

                {!isPending && (
                  <span
                    style={{
                      fontSize: 11,
                      padding: '3px 10px',
                      borderRadius: 10,
                      height: 'fit-content',
                      fontWeight: 600,
                      background: n.status === 'approved' ? '#eef2f8' : '#f2f2f2',
                      color: n.status === 'approved' ? KA.navy : KA.muted,
                    }}
                  >
                    {n.status === 'approved' ? 'Approved' : 'Dismissed'}
                    {n.decidedByEmail ? ` · ${n.decidedByEmail}` : ''}
                  </span>
                )}
              </div>

              {/* recipients */}
              <div style={{ marginTop: 10, fontSize: 12 }}>
                <div style={{ color: KA.muted, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10, marginBottom: 4 }}>
                  Who would be notified
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {Object.entries(n.recipients || {}).map(([role, val]) => (
                    <span
                      key={role}
                      style={{ padding: '2px 8px', borderRadius: 10, background: '#eef2f8', color: KA.navy }}
                    >
                      {ROLE_LABELS[role] || role}: {Array.isArray(val) ? val.join(', ') : val}
                    </span>
                  ))}
                  {missing.map((role) => (
                    <span
                      key={role}
                      style={{ padding: '2px 8px', borderRadius: 10, background: '#fdf0ef', color: KA.red, fontWeight: 600 }}
                    >
                      {ROLE_LABELS[role] || role}: no address on file
                    </span>
                  ))}
                  {n.isDayStudent && (
                    <span style={{ padding: '2px 8px', color: KA.muted }}>Day student — no house head</span>
                  )}
                </div>
              </div>

              {blocked && (
                <div style={{ marginTop: 10, padding: '8px 10px', background: KA.wash, fontSize: 12, borderLeft: `3px solid ${KA.gold}` }}>
                  Now below the threshold at {n.currentTotal} points — a correction has come through
                  since this was raised. Recalculate and re-check before approving.
                </div>
              )}

              {isPending && canWrite && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                  <Btn
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => onDecide(n._id, 'dismissed')}
                  >
                    Dismiss
                  </Btn>
                  <Btn
                    size="sm"
                    disabled={busy || blocked || incomplete}
                    title={
                      blocked
                        ? 'Below the threshold — recalculate first'
                        : incomplete
                        ? `Missing an address for: ${missing.join(', ')}`
                        : undefined
                    }
                    onClick={() => onDecide(n._id, 'approved')}
                  >
                    {busy ? 'Working…' : 'Approve'}
                  </Btn>
                </div>
              )}
            </div>
          );
        })}
      </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Empty state                                                         */
/* ------------------------------------------------------------------ */

function EmptyState({ canWrite, onRebuild, rebuilding, seasonId }) {
  return (
    <div
      style={{
        marginTop: 24,
        padding: 40,
        textAlign: 'center',
        border: `1px dashed ${KA.line}`,
        borderRadius: 6,
        background: KA.wash,
      }}
    >
      <div style={{ fontFamily: DISPLAY, fontSize: 19, color: KA.navy }}>
        Nothing tallied for this season yet
      </div>
      <p style={{ color: KA.muted, fontSize: 13, maxWidth: 460, margin: '10px auto 18px' }}>
        Points are counted overnight from attendance records. Recalculate now to build the first
        tally from everything already in Firestore.
      </p>
      {canWrite ? (
        <Btn onClick={onRebuild} disabled={rebuilding || !seasonId}>
          {rebuilding ? 'Recalculating…' : 'Recalculate now'}
        </Btn>
      ) : (
        <div style={{ fontSize: 13, color: KA.muted }}>
          Ask a dean or administrator to run the first recalculation.
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Student drill-down                                                  */
/* ------------------------------------------------------------------ */

function StudentDetail({ student, personId, events, loading, canWrite, voiding, onClose, onVoid, inline, reportCtx }) {
  const tardies = events.filter((e) => e.source === 'tardy');
  const byBlock = {};
  tardies.forEach((e) => {
    const k = e.block || '—';
    byBlock[k] = (byBlock[k] || 0) + 1;
  });
  const worstBlock = Object.entries(byBlock).sort((a, b) => b[1] - a[1])[0];

  return (
    <div
      style={
        inline
          ? {
              borderTop: `3px solid ${KA.gold}`,
              borderBottom: `1px solid ${KA.line}`,
              background: KA.white,
            }
          : {
              marginTop: 24,
              border: `1px solid ${KA.line}`,
              borderTop: `3px solid ${KA.gold}`,
              borderRadius: 6,
              background: KA.white,
            }
      }
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 18px',
          borderBottom: `1px solid ${KA.line}`,
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <div>
          <div style={{ fontFamily: DISPLAY, fontSize: 20, color: KA.navy }}>
            {(student && student.person) || `Student ${personId}`}
          </div>
          <div style={{ fontSize: 12, color: KA.muted, marginTop: 2 }}>
            ID {personId}
            {student && student.grade != null ? ` · Grade ${student.grade}` : ''}
            {student && student.advisorName ? ` · ${student.advisorName}` : ''}
            {worstBlock ? ` · most often late to Block ${worstBlock[0]} (${worstBlock[1]}×)` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {reportCtx && (
            <>
              <Btn
                variant="outline"
                size="sm"
                title="Open a print dialog — Save as PDF lives there too"
                onClick={() =>
                  printStudentReport({ student, events, ...reportCtx })
                }
              >
                Print
              </Btn>
              <Btn
                variant="ghost"
                size="sm"
                title="Download the report as a PDF file"
                onClick={() =>
                  downloadStudentReport({ student, events, ...reportCtx })
                }
              >
                Save PDF
              </Btn>
            </>
          )}
          <Btn variant="ghost" size="sm" onClick={onClose}>
            Close
          </Btn>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 24, color: KA.muted, fontSize: 13 }}>Loading history…</div>
      ) : events.length === 0 ? (
        <div style={{ padding: 24, color: KA.muted, fontSize: 13 }}>
          No points recorded this season.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 700 }}>
            <thead>
              <tr style={{ background: KA.wash, textAlign: 'left' }}>
                <Th>Date</Th>
                <Th>Type</Th>
                <Th>Detail</Th>
                <Th>Block</Th>
                <Th>Reported by</Th>
                <Th align="right">Points</Th>
                {canWrite && <Th />}
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e._id} style={{ borderTop: `1px solid ${KA.line}` }}>
                  <Td style={{ whiteSpace: 'nowrap' }}>{e.date}</Td>
                  <Td>
                    <span
                      style={{
                        fontSize: 11,
                        padding: '2px 8px',
                        borderRadius: 10,
                        fontWeight: 600,
                        background:
                          e.source === 'tardy'
                            ? '#eef2f8'
                            : e.source === 'phone'
                            ? '#fdf0ef'
                            : '#f5f2e9',
                        color:
                          e.source === 'tardy' ? KA.navy : e.source === 'phone' ? KA.red : KA.gold,
                      }}
                    >
                      {e.source === 'tardy'
                        ? 'Tardy'
                        : e.source === 'phone'
                        ? 'Phone'
                        : e.category || 'Other'}
                    </span>
                  </Td>
                  <Td style={{ color: KA.muted }}>{e.class || e.reason || '—'}</Td>
                  <Td>
                    {e.block ||
                      (e.slot != null ? `Slot ${e.slot}` : null) ||
                      e.station ||
                      '—'}
                  </Td>
                  <Td style={{ color: KA.muted }}>{e.teacherName || e.issuedBy || '—'}</Td>
                  <Td align="right" style={{ fontWeight: 600 }}>
                    {e.points}
                  </Td>
                  {canWrite && (
                    <Td align="right">
                      {e.source === 'manual' ? (
                        <Btn
                          variant="ghost"
                          size="sm"
                          disabled={voiding === e.entryId}
                          onClick={() => onVoid(e.entryId)}
                          title="Void this point so it stops counting"
                        >
                          {voiding === e.entryId ? 'Voiding\u2026' : 'Void'}
                        </Btn>
                      ) : (
                        <span
                          style={{ fontSize: 11, color: KA.muted }}
                          title="Tardies come from attendance. Correct the record in Veracross instead."
                        >
                          from Veracross
                        </span>
                      )}
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tardies by teacher                                                  */
/* ------------------------------------------------------------------ */

/**
 * Who is issuing tardies, from the teacherTardies map the rebuild writes.
 *
 * Shows a per-student rate alongside the raw count: a teacher with 60
 * tardies across 55 students is describing their block, while 60 across 6
 * students is describing six students. The raw number alone conflates them.
 */
function TeacherTardyModal({ teacherTardies, season, onClose, onFilter }) {
  const [sortKey, setSortKey] = useState('tardies');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    const list = Object.entries(teacherTardies || {}).map(([name, v]) => ({
      name,
      teacherEmail: v.teacherEmail || null,
      tardies: Number(v.tardies) || 0,
      studentCount: Number(v.studentCount) || 0,
      tardiesPerStudent: Number(v.tardiesPerStudent) || 0,
      blocks: v.blocks || {},
      classes: v.classes || {},
      studentIds: v.studentIds || [],
      lastDate: v.lastDate || null,
    }));
    const term = search.trim().toLowerCase();
    const filtered = term ? list.filter((r) => r.name.toLowerCase().includes(term)) : list;
    const dir = sortDir === 'asc' ? 1 : -1;
    return filtered.sort((a, b) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name) * dir;
      return (a[sortKey] - b[sortKey]) * dir || a.name.localeCompare(b.name);
    });
  }, [teacherTardies, sortKey, sortDir, search]);

  const totals = useMemo(
    () => ({
      teachers: rows.length,
      tardies: rows.reduce((s, r) => s + r.tardies, 0),
    }),
    [rows]
  );

  function toggle(k) {
    if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(k);
      setSortDir(k === 'name' ? 'asc' : 'desc');
    }
  }

  // The PDF is printed in whatever order is on screen, so it says which.
  const SORT_LABELS = {
    name: 'teacher name',
    tardies: 'tardies',
    studentCount: 'student count',
    tardiesPerStudent: 'tardies per student',
    lastDate: 'most recent tardy',
  };
  const sortLabel = `${SORT_LABELS[sortKey] || sortKey}, ${
    sortDir === 'asc' ? 'lowest first' : 'highest first'
  }`;

  const pdfArgs = () => ({
    rows,
    season,
    sortLabel,
    generatedBy: (getAuth().currentUser && getAuth().currentUser.email) || null,
  });

  function exportCsv() {
    const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const head = ['Teacher', 'Email', 'Tardies', 'Students', 'Per student', 'Blocks', 'Most frequent class', 'Last tardy'];
    const body = rows.map((r) => {
      const topClass = Object.entries(r.classes).sort((a, b) => b[1] - a[1])[0];
      const blocks = Object.entries(r.blocks)
        .sort((a, b) => b[1] - a[1])
        .map(([b, n]) => `${b}:${n}`)
        .join(' ');
      return [r.name, r.teacherEmail, r.tardies, r.studentCount, r.tardiesPerStudent, blocks,
              topClass ? `${topClass[0]} (${topClass[1]})` : '', r.lastDate]
        .map(esc)
        .join(',');
    });
    const blob = new Blob([[head.map(esc).join(','), ...body].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tardies-by-teacher-${season.id || 'season'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const SortTh = ({ k, children, align }) => (
    <th
      onClick={() => toggle(k)}
      style={{
        padding: '9px 10px',
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '.05em',
        color: sortKey === k ? KA.navy : KA.muted,
        fontWeight: 600,
        cursor: 'pointer',
        textAlign: align || 'left',
        whiteSpace: 'nowrap',
        userSelect: 'none',
      }}
    >
      {children}
      {sortKey === k && <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(26,26,26,0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 20,
        zIndex: 1000,
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: KA.white,
          borderRadius: 6,
          borderTop: `3px solid ${KA.navy}`,
          padding: 24,
          width: '100%',
          maxWidth: 900,
          margin: 'auto',
          fontFamily: BODY,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ fontFamily: DISPLAY, color: KA.navy, margin: '0 0 4px', fontWeight: 400, fontSize: 21 }}>
              Tardies by teacher
            </h3>
            <div style={{ fontSize: 12, color: KA.muted }}>
              {season.label || season.id} · {totals.tardies} unexcused tardies across{' '}
              {totals.teachers} teacher{totals.teachers === 1 ? '' : 's'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              placeholder="Search teacher"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...inputStyle, width: 170 }}
            />
            <Btn
              variant="outline"
              size="sm"
              onClick={() => printTeacherReport(pdfArgs())}
              disabled={!rows.length}
              title="Open a print dialog — Save as PDF lives there too"
            >
              Print
            </Btn>
            <Btn variant="ghost" size="sm" onClick={() => downloadTeacherReport(pdfArgs())} disabled={!rows.length}>
              Save PDF
            </Btn>
            <Btn variant="ghost" size="sm" onClick={exportCsv} disabled={!rows.length}>
              Export CSV
            </Btn>
            <Btn variant="ghost" size="sm" onClick={onClose}>
              Close
            </Btn>
          </div>
        </div>

        {!rows.length && (
          <div style={{ padding: 36, textAlign: 'center', color: KA.muted, fontSize: 13, marginTop: 16 }}>
            {teacherTardies
              ? 'No tardies recorded this term.'
              : 'Recalculate to build the teacher breakdown.'}
          </div>
        )}

        {rows.length > 0 && (
          <div style={{ marginTop: 16, border: `1px solid ${KA.line}`, borderRadius: 6, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: KA.wash, textAlign: 'left' }}>
                  <th style={{ padding: '9px 10px', width: 28 }} />
                  <SortTh k="name">Teacher</SortTh>
                  <SortTh k="tardies" align="right">Tardies</SortTh>
                  <SortTh k="studentCount" align="right">Students</SortTh>
                  <SortTh k="tardiesPerStudent" align="right">Per student</SortTh>
                  <th style={{ padding: '9px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: KA.muted, fontWeight: 600 }}>
                    Blocks
                  </th>
                  <SortTh k="lastDate">Last</SortTh>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const blocks = Object.entries(r.blocks).sort((a, b) => b[1] - a[1]);
                  return (
                    <tr
                      key={r.name}
                      onClick={() => r.studentIds.length && onFilter(r.name, r.studentIds)}
                      title={r.studentIds.length ? 'Show these students in the roster' : undefined}
                      style={{
                        borderTop: `1px solid ${KA.line}`,
                        cursor: r.studentIds.length ? 'pointer' : 'default',
                      }}
                    >
                      <td style={{ padding: '8px 10px', color: KA.muted, fontVariantNumeric: 'tabular-nums' }}>
                        {i + 1}
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <div style={{ fontWeight: 600, color: KA.navy }}>{r.name}</div>
                        {r.teacherEmail && (
                          <div style={{ fontSize: 11, color: KA.muted }}>{r.teacherEmail}</div>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: KA.red }}>
                        {r.tardies}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}>{r.studentCount}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: KA.muted }}>
                        {r.tardiesPerStudent}
                      </td>
                      <td style={{ padding: '8px 10px', fontSize: 11, color: KA.muted }}>
                        {blocks.slice(0, 4).map(([b, n]) => `${b}·${n}`).join('  ') || '—'}
                      </td>
                      <td style={{ padding: '8px 10px', color: KA.muted, fontSize: 12 }}>
                        {r.lastDate || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ fontSize: 11, color: KA.muted, marginTop: 14 }}>
          Click a row to filter the roster to that teacher's students. <strong>Per student</strong>
          {' '}separates a teacher seeing many students once from one seeing a few students often —
          the raw count alone reads the same for both.
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Phone history preview (read-only)                                   */
/* ------------------------------------------------------------------ */

/**
 * Runs previewPhoneViolations and shows what WOULD be counted. Nothing here
 * writes: no MOPs, no notifications, no summary changes. It exists so the
 * early-term data can be examined without the matching rules ever entering
 * /config/mops, where a forgotten value would turn history into real points.
 */
function PhonePreviewModal({ fns, seasonId, seasonStart, onClose }) {
  const today = new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(seasonStart || '2026-08-23');
  const [to, setTo] = useState(today);
  const [useNote, setUseNote] = useState(true);
  const [notePattern, setNotePattern] = useState('reported missing');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [res, setRes] = useState(null);

  async function run() {
    setBusy(true);
    setErr(null);
    setRes(null);
    try {
      const fn = httpsCallable(fns, 'previewPhoneViolations');
      const out = await fn({
        seasonId,
        from,
        to,
        notePatterns: useNote && notePattern.trim() ? [notePattern.trim()] : [],
      });
      setRes(out.data);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  function downloadCsv(r) {
    const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const head = ['Student', 'SIS ID', 'Grade', 'Matched', 'Counted', 'Via note', 'Via status', 'Dates'];
    const body = (r.students || []).map((t) =>
      [t.person, t.personId, t.grade, t.matched, t.counted, t.viaNote, t.viaStatus, (t.days || []).join(' ')]
        .map(esc)
        .join(',')
    );
    const blob = new Blob([[head.map(esc).join(','), ...body].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `phone-preview-${r.from}_to_${r.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const K = (label, value) => (
    <div style={{ marginRight: 22, marginBottom: 8 }}>
      <div style={{ fontSize: 20, fontFamily: DISPLAY, color: KA.navy }}>{value}</div>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em', color: KA.muted }}>
        {label}
      </div>
    </div>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(26,26,26,0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 20,
        zIndex: 1000,
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: KA.white,
          borderRadius: 6,
          borderTop: `3px solid ${KA.gold}`,
          padding: 24,
          width: '100%',
          maxWidth: 780,
          margin: 'auto',
          fontFamily: BODY,
        }}
      >
        <h3 style={{ fontFamily: DISPLAY, color: KA.navy, margin: '0 0 4px', fontWeight: 400 }}>
          Phone history preview
        </h3>
        <div style={{ fontSize: 12, color: KA.muted, marginBottom: 16 }}>
          Shows what would be counted over a date range. Writes nothing — no points, no
          notifications, no change to any total.
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Field label="From">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ ...inputStyle, width: 160 }} />
          </Field>
          <Field label="To">
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ ...inputStyle, width: 160 }} />
          </Field>
          <div style={{ marginBottom: 12 }}>
            <Btn onClick={run} disabled={busy}>
              {busy ? 'Scanning…' : 'Run preview'}
            </Btn>
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 6 }}>
          <input type="checkbox" checked={useNote} onChange={(e) => setUseNote(e.target.checked)} />
          Also count older records whose note contains
          <input
            value={notePattern}
            onChange={(e) => setNotePattern(e.target.value)}
            disabled={!useNote}
            style={{ ...inputStyle, width: 190, padding: '4px 8px' }}
          />
        </label>
        <div style={{ fontSize: 11, color: KA.muted, marginBottom: 16 }}>
          Before the kiosk wrote an explicit status, a miss was recorded as{' '}
          <code>pending</code> with a note. Leave this off to see only what the live rules
          would catch.
        </div>

        {err && (
          <div style={{ padding: '10px 14px', background: '#fdf0ef', borderLeft: `3px solid ${KA.red}`, fontSize: 13, marginBottom: 14 }}>
            {err}
          </div>
        )}

        {res && (
          <div style={{ borderTop: `1px solid ${KA.line}`, paddingTop: 14 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {K('Matched', res.matchedAll)}
              {K('Would count', res.violations)}
              {K('Students', res.studentsWithAny)}
              {K('Days found', res.daysFound)}
              {K('Dropped (capped)', res.droppedOverCap)}
            </div>

            {res.droppedOverCap > 0 && (
              <div
                style={{
                  padding: '10px 12px',
                  background: KA.wash,
                  borderLeft: `3px solid ${KA.gold}`,
                  fontSize: 12,
                  marginBottom: 12,
                }}
              >
                <strong>{res.droppedOverCap}</strong> matches fell on days over the daily cap and
                would not be counted. They are still listed below, with their dates marked{' '}
                <code>*</code>. A cap tripping on most days usually means the rule is matching
                something broader than a real violation — worth resolving before any of this is
                counted for real.
              </div>
            )}

            <div style={{ fontSize: 12, marginTop: 6 }}>
              <strong style={{ color: KA.navy }}>Statuses found:</strong>{' '}
              <span style={{ color: KA.muted }}>
                {Object.entries(res.statusCounts || {})
                  .map(([k, v]) => `${k} (${v})`)
                  .join(' · ') || 'none'}
              </span>
            </div>
            {Object.keys(res.noteCounts || {}).length > 0 && (
              <div style={{ fontSize: 12, marginTop: 4 }}>
                <strong style={{ color: KA.navy }}>Notes found:</strong>{' '}
                <span style={{ color: KA.muted }}>
                  {Object.entries(res.noteCounts)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 6)
                    .map(([k, v]) => `"${k}" (${v})`)
                    .join(' · ')}
                </span>
              </div>
            )}

            <div style={{ fontSize: 12, marginTop: 10 }}>
              <strong style={{ color: KA.navy }}>Would reach a phone rung:</strong>{' '}
              <span style={{ color: KA.muted }}>
                {Object.entries(res.strikeProjection || {})
                  .map(([k, v]) => `${v} at ${k}+`)
                  .join(' · ') || 'none'}
              </span>
            </div>
            {res.droppedOverCap > 0 && (
              <div style={{ fontSize: 12, marginTop: 4 }}>
                <strong style={{ color: KA.navy }}>If capped days were included:</strong>{' '}
                <span style={{ color: KA.red }}>
                  {Object.entries(res.strikeProjectionAll || {})
                    .map(([k, v]) => `${v} at ${k}+`)
                    .join(' · ')}
                </span>
              </div>
            )}

            {res.mopProjection && (
              <div style={{ fontSize: 12, marginTop: 6 }}>
                <strong style={{ color: KA.navy }}>Combined with current MOPs:</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18, color: KA.muted }}>
                  {Object.entries(res.mopProjection).map(([pts, m]) => (
                    <li key={pts}>
                      {m.label} ({pts}): {m.nowAtOrAbove} now → <strong>{m.wouldBeAtOrAbove}</strong>{' '}
                      if these were counted
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {res.students && res.students.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    marginBottom: 6,
                  }}
                >
                  <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: KA.muted }}>
                    Students involved ({res.students.length})
                  </div>
                  <Btn variant="ghost" size="sm" onClick={() => downloadCsv(res)}>
                    Download CSV
                  </Btn>
                </div>
                <div style={{ maxHeight: 260, overflowY: 'auto', border: `1px solid ${KA.line}`, borderRadius: 4 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: KA.wash, textAlign: 'left', position: 'sticky', top: 0 }}>
                        <Th align="right">#</Th>
                        <Th>Student</Th>
                        <Th>ID</Th>
                        <Th align="right">Grade</Th>
                        <Th align="right">Matched</Th>
                        <Th align="right">Counted</Th>
                        <Th>Dates</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {res.students.map((t, i) => (
                        <tr key={t.personId} style={{ borderTop: `1px solid ${KA.line}` }}>
                          <Td align="right" style={{ color: KA.muted, fontVariantNumeric: 'tabular-nums' }}>
                            {i + 1}
                          </Td>
                          <Td style={{ fontWeight: 600, color: KA.navy, whiteSpace: 'nowrap' }}>
                            {t.person || '—'}
                          </Td>
                          <Td style={{ color: KA.muted }}>{t.personId}</Td>
                          <Td align="right">{t.grade ?? '—'}</Td>
                          <Td align="right" style={{ fontWeight: 600 }}>{t.matched}</Td>
                          <Td
                            align="right"
                            style={{ color: t.counted === 0 ? KA.muted : KA.ink }}
                          >
                            {t.counted}
                          </Td>
                          <Td style={{ color: KA.muted, fontSize: 11 }}>
                            {(t.days || []).join(', ')}
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ fontSize: 11, color: KA.muted, marginTop: 4 }}>
                  A date marked <code>*</code> fell on a day over the cap — matched, but not counted.
                </div>
              </div>
            )}

            <div style={{ marginTop: 14, maxHeight: 220, overflowY: 'auto', border: `1px solid ${KA.line}`, borderRadius: 4 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: KA.wash, textAlign: 'left' }}>
                    <Th>Date</Th>
                    <Th align="right">Records</Th>
                    <Th align="right">Matched</Th>
                    <Th align="right">Counted</Th>
                    <Th>Skipped</Th>
                  </tr>
                </thead>
                <tbody>
                  {res.perDay.map((p) => (
                    <tr key={p.date} style={{ borderTop: `1px solid ${KA.line}`, color: p.skipped ? KA.muted : KA.ink }}>
                      <Td>{p.date}</Td>
                      <Td align="right">{p.recordsOnDay}</Td>
                      <Td align="right">{p.matched}</Td>
                      <Td align="right" style={{ fontWeight: 600 }}>{p.wouldCount}</Td>
                      <Td style={{ color: p.skipped ? KA.red : KA.muted }}>{p.skipped || '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <Btn variant="ghost" onClick={onClose}>Close</Btn>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Manual MOP entry                                                    */
/* ------------------------------------------------------------------ */

function AddMopModal({ fns, seasonLabel, onClose, onSaved, onError }) {
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [personId, setPersonId] = useState('');
  const [pickerText, setPickerText] = useState('');
  const [date, setDate] = useState(new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10));
  const [category, setCategory] = useState(MANUAL_CATEGORIES[0]);
  const [points, setPoints] = useState(1);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'students'));
        if (!alive) return;
        const list = [];
        snap.forEach((d) => {
          const s = d.data() || {};
          list.push({
            id: d.id,
            name: s.fullName || [s.firstName, s.lastName].filter(Boolean).join(' ') || d.id,
          });
        });
        list.sort((a, b) => a.name.localeCompare(b.name));
        setStudents(list);
      } catch (err) {
        onError(`Could not load the student list. ${err.message}`);
      } finally {
        if (alive) setLoadingStudents(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [onError]);

  const matches = useMemo(() => {
    const t = pickerText.trim().toLowerCase();
    if (!t) return [];
    return students
      .filter((s) => s.name.toLowerCase().includes(t) || s.id.includes(t))
      .slice(0, 8);
  }, [pickerText, students]);

  const chosen = students.find((s) => s.id === personId);

  async function save() {
    if (!personId) return onError('Choose a student first.');
    setSaving(true);
    try {
      const fn = httpsCallable(fns, 'addManualMop');
      await fn({
        personId,
        person: chosen ? chosen.name : null,
        date,
        category,
        reason: category,
        points: Number(points),
        notes: notes.trim() || null,
      });
      onSaved(
        `Added ${points} point${Number(points) === 1 ? '' : 's'} for ${
          chosen ? chosen.name : personId
        }. Recalculate to update the table.`
      );
    } catch (err) {
      onError(`Could not save that point. ${err.message}`);
      setSaving(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(26,26,26,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: KA.white,
          borderRadius: 6,
          borderTop: `3px solid ${KA.red}`,
          padding: 24,
          width: '100%',
          maxWidth: 440,
          maxHeight: '90vh',
          overflowY: 'auto',
          fontFamily: BODY,
        }}
      >
        <h3 style={{ fontFamily: DISPLAY, color: KA.navy, margin: '0 0 4px', fontWeight: 400 }}>
          Add a point
        </h3>
        <div style={{ fontSize: 12, color: KA.muted, marginBottom: 18 }}>
          For anything other than a tardy. Tardies are counted automatically from attendance.
        </div>

        <Field label="Student">
          {chosen ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '7px 9px',
                border: `1px solid ${KA.navy}`,
                borderRadius: 4,
                fontSize: 13,
              }}
            >
              <span>
                {chosen.name} <span style={{ color: KA.muted }}>· {chosen.id}</span>
              </span>
              <Btn variant="ghost" size="sm" onClick={() => { setPersonId(''); setPickerText(''); }}>
                Change
              </Btn>
            </div>
          ) : (
            <>
              <input
                autoFocus
                placeholder={loadingStudents ? 'Loading students…' : 'Type a name or ID'}
                disabled={loadingStudents}
                value={pickerText}
                onChange={(e) => setPickerText(e.target.value)}
                style={inputStyle}
              />
              {pickerText.trim() && matches.length === 0 && !loadingStudents && (
                <div style={{ padding: '7px 9px', fontSize: 12, color: KA.muted }}>
                  No student matches that name or ID.
                </div>
              )}
              {matches.length > 0 && (
                <div style={{ border: `1px solid ${KA.line}`, borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
                  {matches.map((s) => (
                    <div
                      key={s.id}
                      role="button"
                      tabIndex={0}
                      // onMouseDown, not onClick: it fires before the input's
                      // blur, so the row can't be torn out from under the click.
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setPersonId(s.id);
                        setPickerText('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setPersonId(s.id);
                          setPickerText('');
                        }
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f4f7fb'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      style={{
                        padding: '7px 9px',
                        fontSize: 13,
                        cursor: 'pointer',
                        borderTop: `1px solid ${KA.line}`,
                        background: 'transparent',
                      }}
                    >
                      {s.name} <span style={{ color: KA.muted }}>· {s.id}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Field>

        <Field label="Date">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
        </Field>

        <Field label="Reason">
          <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle}>
            {MANUAL_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Points">
          <input
            type="number"
            min={1}
            max={20}
            value={points}
            onChange={(e) => setPoints(e.target.value)}
            style={inputStyle}
          />
        </Field>

        <Field label="Notes (optional)">
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
        </Field>

        <div style={{ fontSize: 11, color: KA.muted, marginBottom: 14 }}>
          Recorded against {seasonLabel}. Your name is saved with the entry.
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Btn>
          <Btn onClick={save} disabled={saving || !personId}>
            {saving ? 'Saving…' : 'Add point'}
          </Btn>
        </div>
      </div>
    </div>
  );
}